import { Icon } from './Icon.js';
import { iconAdd, iconChevron, iconChevronLeft, iconDelete } from './generated/icons.js';
import { LoadingIndicator } from './LoadingIndicator.js';
import { cx } from './util.js';

export interface PhotoItem {
  id: string;
  url: string | null;
  status: 'ready' | 'uploading' | 'processing' | 'rejected';
  alt: string;
}

export interface PhotoGridProps {
  photos: PhotoItem[];
  label: string;
  onMove?: (id: string, direction: -1 | 1) => void;
  onRemove?: (id: string) => void;
  /** Upload progress, 0 to 100, shown as a tile after the photos. */
  uploading?: { percent: number; label: string };
  /** The tile after the photos that adds one. */
  add?: { label: string; onClick: () => void; disabled?: boolean };
  labels: {
    earlier: string;
    later: string;
    remove: string;
    cover: string;
    processing: string;
    rejected: string;
  };
}

/** Photos with reorder by button (and so by keyboard), a cover mark, and processing states. */
export function PhotoGrid({
  photos,
  label,
  onMove,
  onRemove,
  uploading,
  add,
  labels,
}: PhotoGridProps) {
  return (
    <ul className="k-photos" aria-label={label}>
      {photos.map((photo, index) => (
        <li key={photo.id} className={cx('k-photo', `k-photo--${photo.status}`)}>
          {photo.status === 'ready' && photo.url !== null ? (
            <img src={photo.url} alt={photo.alt} loading="lazy" decoding="async" />
          ) : photo.status === 'rejected' ? (
            <span className="k-photo__note k-t-label-sm">{labels.rejected}</span>
          ) : (
            <span className="k-photo__note">
              <LoadingIndicator size={28} label={labels.processing} />
            </span>
          )}
          {index === 0 && photo.status === 'ready' ? (
            <span className="k-photo__cover k-t-label-sm">{labels.cover}</span>
          ) : null}
          <span className="k-photo__actions">
            {onMove === undefined ? null : (
              <>
                <button
                  type="button"
                  className="k-iconbtn k-layer"
                  aria-label={labels.earlier}
                  disabled={index === 0}
                  onClick={() => onMove(photo.id, -1)}
                >
                  <Icon icon={iconChevronLeft} size={20} />
                </button>
                <button
                  type="button"
                  className="k-iconbtn k-layer"
                  aria-label={labels.later}
                  disabled={index === photos.length - 1}
                  onClick={() => onMove(photo.id, 1)}
                >
                  <Icon icon={iconChevron} size={20} />
                </button>
              </>
            )}
            {onRemove === undefined ? null : (
              <button
                type="button"
                className="k-iconbtn k-layer"
                aria-label={labels.remove}
                onClick={() => onRemove(photo.id)}
              >
                <Icon icon={iconDelete} size={20} />
              </button>
            )}
          </span>
        </li>
      ))}
      {uploading === undefined ? null : (
        <li className="k-photo k-photo--flat">
          <span
            className="k-photo__bar"
            role="progressbar"
            aria-label={uploading.label}
            aria-valuenow={Math.round(uploading.percent)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <i style={{ width: `${String(uploading.percent)}%` }} />
          </span>
          <span className="k-t-label-sm">{`${String(Math.round(uploading.percent))}%`}</span>
        </li>
      )}
      {add === undefined ? null : (
        <li className="k-photo k-photo--flat">
          <button
            type="button"
            className="k-photo__add k-layer"
            disabled={add.disabled === true}
            onClick={add.onClick}
          >
            <Icon icon={iconAdd} />
            <span className="k-t-label-sm">{add.label}</span>
          </button>
        </li>
      )}
    </ul>
  );
}
